import { Injectable } from '@angular/core';

import { environment } from '../../../../../environments/environment';

/* ═══════════ Chatvion (WhatsApp) device link — REST client ═══════════
   The browser never talks to chatvion.com directly. It calls the Trexa API's
   server-side proxy at `{apiOrigin}/api/chatvion/**`, which forwards to
   `https://chatvion.com/v1/**` with the tenant-whitelisting header. Auth to our
   own proxy is the same JWT the GraphQL SDK uses (stored in the auth session);
   we attach it as `Authorization: Bearer …` on every call.

   This mirrors Skiddo's `WhatsAppDeviceService`, but uses raw `fetch` + Promises
   (Trexa provides no HttpClient / interceptors — see GraphqlApiService) so it drops
   straight into the signal-based components without RxJS. */

export interface CreateLinkSessionParams {
  optionalDisplayName?: string;
  accountId?: string;
}

export interface LinkSession {
  sessionId: string;
  accountId: string;
  qrCodeData: string | null;
  expiresAt: string;
}

export interface LinkSessionStatus {
  status: string;              // CONNECTED | EXPIRED | ERROR | (pending/QR)
  accountId?: string;
  error?: string;
  qrCodeData?: string;
}

export interface InstanceListItem {
  id: string;                  // Chatvion account id (used in URLs)
  displayName: string;
  phone: string | null;
  jid: string | null;
  status: string;
  instanceId: string | null;   // wa_… connector id (used when sending)
  port: number | null;
  lastSeenAt: string | null;
  isDefault?: boolean;
}

/** A device that can actually deliver: Chatvion says CONNECTED *and* it has a WhatsApp account
 *  behind it. A CONNECTED row with no phone/jid is a ghost slot (QA 10-30: `wa_3e6ee8b9…`) —
 *  every send through it sat in WAITING_CONNECTOR until it aged out to "Queue age exceeded". */
export function isLiveInstance(inst: Pick<InstanceListItem, 'status' | 'phone' | 'jid'> | null | undefined): boolean {
  return !!inst && inst.status === 'CONNECTED' && !!(inst.phone || inst.jid);
}

/** Per-recipient status stored in WhatsAppSendLog.RecipientsJson. QUEUED = Chatvion accepted it but
 *  has not delivered it yet; the History tab re-checks it against Chatvion's log (`logId`). */
export type WaRecipientStatus = 'SENT' | 'QUEUED' | 'FAILED';

export function recipientStatusOf(res: { accepted: boolean; delivered: boolean }): WaRecipientStatus {
  return res.delivered ? 'SENT' : res.accepted ? 'QUEUED' : 'FAILED';
}

/** Event-level status for a send row: Sent only once EVERY recipient is delivered. */
export function eventStatusOf(statuses: WaRecipientStatus[]): 'Sent' | 'Queued' | 'Partial' | 'Failed' {
  if (!statuses.length) return 'Failed';
  if (statuses.every(s => s === 'SENT')) return 'Sent';
  if (statuses.some(s => s === 'QUEUED')) return 'Queued';
  return statuses.some(s => s === 'SENT') ? 'Partial' : 'Failed';
}

export interface InstanceList {
  instances: InstanceListItem[];
  deviceLimit?: number;
  deviceCount?: number;
}

export interface InstanceDetail {
  id: string;
  displayName: string;
  phone: string | null;
  jid: string | null;
  status: string;
  instanceId: string;
  port: number | null;
  maskedAccessToken?: string;
  webhook?: { enabled: boolean; url?: string; events?: string[] };
}

/** Envelope Chatvion wraps every response in. */
interface Envelope<T> { data: T; }

const SESSION_KEY = 'trexa.auth.session';

@Injectable({ providedIn: 'root' })
export class WhatsAppDeviceService {
  /** `{apiOrigin}/api/chatvion` — apiOrigin is the GraphQL endpoint minus `/graphql`. */
  private get baseUrl(): string {
    const gql = (environment.graphqlEndpoint || '').replace(/\/+$/, '');
    const origin = gql.replace(/\/graphql$/i, '');
    return `${origin}/api/chatvion`;
  }

  private get tenantId(): string {
    // Trexa's whitelisted Chatvion tenant. Overridable per-env via environment.wasapBotTenantId.
    return (environment as { wasapBotTenantId?: string }).wasapBotTenantId ?? 'demo-tenant';
  }

  private get tenantPath(): string {
    return `${this.baseUrl}/tenants/${this.tenantId}/whatsapp`;
  }

  private authToken(): string | null {
    // Primary: the real login session (same token the GraphQL SDK uses). Prod/staging always take this.
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      const token = raw ? (JSON.parse(raw) as { accessToken?: string }).accessToken ?? null : null;
      if (token) return token;
    } catch { /* fall through to dev token */ }
    // Dev fallback: environment.devAuthToken is a long-lived token for this API (empty in prod),
    // so the WhatsApp Device tab is testable in `ng serve` without a full admin login.
    const dev = (environment as { devAuthToken?: string }).devAuthToken;
    return dev && dev.length ? dev : null;
  }

  private async send<T>(method: string, url: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = this.authToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'include',
    });

    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let json: unknown = null;
    try { json = text ? JSON.parse(text) : null; } catch { json = null; }

    if (!res.ok) {
      const msg = (json as { message?: string; error?: string })?.message
        ?? (json as { error?: string })?.error
        ?? `Request failed (${res.status})`;
      throw new Error(msg);
    }
    return json as T;
  }

  /* ═══════════ link-session (QR) ═══════════ */
  async createLinkSession(params?: CreateLinkSessionParams): Promise<LinkSession> {
    const res = await this.send<Envelope<LinkSession>>(
      'POST', `${this.tenantPath}/accounts/link-session`, params ?? {});
    return res.data;
  }

  async getLinkSessionStatus(sessionId: string): Promise<LinkSessionStatus> {
    const res = await this.send<Envelope<LinkSessionStatus>>(
      'GET', `${this.tenantPath}/accounts/link-session/${sessionId}`);
    return res.data;
  }

  async cancelLinkSession(sessionId: string): Promise<void> {
    await this.send<unknown>(
      'POST', `${this.tenantPath}/accounts/link-session/${sessionId}/cancel`, {});
  }

  /* ═══════════ instances (devices) ═══════════ */
  async listInstances(): Promise<InstanceList> {
    try {
      const res = await this.send<Envelope<InstanceList>>('GET', `${this.tenantPath}/instances`);
      return res?.data ?? { instances: [], deviceCount: 0, deviceLimit: 1 };
    } catch {
      return { instances: [], deviceCount: 0, deviceLimit: 1 };
    }
  }

  async getInstanceDetail(accountId: string): Promise<InstanceDetail> {
    const res = await this.send<Envelope<InstanceDetail>>(
      'GET', `${this.tenantPath}/instances/${accountId}`);
    return res.data;
  }

  async logoutInstance(accountId: string): Promise<void> {
    await this.send<unknown>('POST', `${this.tenantPath}/instances/${accountId}/logout`, {});
  }

  /** POST …/delete (proxy forwards it as DELETE — browser DELETE is often blocked by IIS/WebDAV). */
  async deleteInstance(accountId: string): Promise<void> {
    await this.send<unknown>('POST', `${this.tenantPath}/instances/${accountId}/delete`, {});
  }

  /* ═══════════ outbound send (real WhatsApp) ═══════════ */
  /**
   * Send one WhatsApp message through the linked device. Goes to Chatvion's
   * `POST /v1/webhooks/whatsapp/send-batch` via the same proxy. `instanceId` selects the device
   * (null → the tenant's default). Phone is normalised to Malaysian format (digits, leading 6).
   * Returns whether Chatvion accepted the message (SENT / QUEUED / WAITING_CONNECTOR all count).
   */
  async sendMessage(instanceId: string | null, phone: string, text: string): Promise<SendResult> {
    const to = '+' + this.sanitizePhone(phone);
    const payload = {
      messages: [{
        // requestId MUST be globally unique per send. Chatvion's send-batch is idempotent on
        // (tenantId, requestId): if a log row with this id already exists it returns THAT row's
        // status + errorMessage instead of sending. The old `trexa-{phone}-{counter}` reset its
        // counter to 1 on every page reload, so the first send to a phone always reused the same id
        // and got back a stale, aged-out row's "Queue age exceeded" error. Stamp the wall-clock time
        // (+ counter to disambiguate same-millisecond sends) so every send is a fresh message.
        requestId: `trexa-${to}-${Date.now()}-${this.nextReqId()}`,
        instanceId: instanceId && instanceId.startsWith('wa_') ? instanceId : null,
        to,
        text,
        metadata: { source: 'trexa', tenantId: this.tenantId },
      }],
      delayMs: 0,
    };
    const res = await this.send<SendBatchResponse>(
      'POST', `${this.baseUrl}/webhooks/whatsapp/send-batch`, payload);
    const r = res?.data?.results?.[0];
    const status = (r?.status ?? '').toUpperCase();
    // SENT / QUEUED / WAITING_CONNECTOR all count as accepted: QUEUED and WAITING_CONNECTOR are the
    // NORMAL async path — the send-batch call returns immediately and the drain worker delivers the
    // message a moment later. Only an outright rejection (FAILED / no success) is a real failure.
    // `delivered` is the separate, stricter fact: only SENT means it reached WhatsApp. Callers must
    // NOT re-send on !delivered (that double-sends a queued message) — it only drives what History
    // shows, which is re-checked later via getDeliveryStatus(logId).
    const accepted = !!r?.success || ['SENT', 'QUEUED', 'WAITING_CONNECTOR'].includes(status);
    return {
      accepted, delivered: status === 'SENT',
      status: r?.status ?? (accepted ? 'SENT' : 'FAILED'), to, error: r?.error, logId: r?.logId,
    };
  }

  /** Chatvion's current status for one sent message (GET …/webhooks/whatsapp/logs/:logId).
   *  Null when the log can't be read — the caller keeps whatever it had. */
  async getDeliveryStatus(logId: string): Promise<{ status: WaRecipientStatus; error: string } | null> {
    try {
      const res = await this.send<Envelope<{ status?: string; errorMessage?: string | null } | null>>(
        'GET', `${this.baseUrl}/webhooks/whatsapp/logs/${encodeURIComponent(logId)}?tenantId=${encodeURIComponent(this.tenantId)}`);
      const log = res?.data;
      if (!log) return null;
      const s = (log.status ?? '').toUpperCase();
      const status: WaRecipientStatus = s === 'SENT' ? 'SENT' : s === 'FAILED' ? 'FAILED' : 'QUEUED';
      return { status, error: log.errorMessage ?? '' };
    } catch {
      return null;
    }
  }

  private reqCounter = 0;
  private nextReqId(): number { return ++this.reqCounter; }

  /** Digits only; force a Malaysian country code by prefixing 6 when the number doesn't already
   *  start with it. A local number like 0169632700 becomes 60169632700 (the leading 0 is kept,
   *  same as Skiddo) — do NOT strip the 0, or you drop a digit and Chatvion rejects the recipient. */
  private sanitizePhone(raw: string): string {
    const d = (raw || '').replace(/\D/g, '');
    return d.startsWith('6') ? d : '6' + d;
  }
}

export interface SendResult {
  /** Chatvion took it (SENT, or queued for async delivery). */
  accepted: boolean;
  /** It actually reached WhatsApp (status SENT) — a queued message is accepted but not delivered. */
  delivered: boolean;
  status: string; to: string; error?: string;
  /** Chatvion waMessageLog id — lets History re-check delivery later. */
  logId?: string;
}

interface SendBatchResponse {
  data?: {
    total?: number; sent?: number; failed?: number;
    results?: Array<{ success?: boolean; status?: string; requestId?: string; error?: string; logId?: string }>;
  };
}
